/**
 * Acesso ao banco local. Implementa o `RepositorioValidacao` que a cadeia de
 * regras consome, e as consultas das telas (setores, ingressos, busca).
 */

import {banco, linhas} from './banco';
import type {RepositorioValidacao} from '../dominio/validar';
import type {
  Evento,
  IngressoCancelado,
  IngressoOnline,
  Lote,
  Setor,
  Validado,
} from '../dominio/tipos';

/** Repositório amarrado a um evento — é sempre um evento por vez na portaria. */
export function repositorioDoEvento(): RepositorioValidacao {
  const db = banco();

  return {
    async buscarCancelado(eventoId, codbar) {
      const r = await db.execute(
        'select eventoId, codbar, dataCancelamento, motivoCancelamento, usuario ' +
          'from ingresso_cancelado where eventoId = ? and codbar = ? limit 1',
        [eventoId, codbar],
      );
      return linhas<IngressoCancelado>(r)[0] ?? null;
    },

    async buscarValidado(codbar) {
      const r = await db.execute(
        'select id, eventoId, codbar, dataHora, setorId, consumacao, enviado ' +
          'from validado where codbar = ? limit 1',
        [codbar],
      );
      const linha = linhas<Omit<Validado, 'enviado'> & {enviado: number}>(r)[0];
      return linha ? {...linha, enviado: Boolean(linha.enviado)} : null;
    },

    /**
     * Testa os candidatos em ordem contra `loteStr` — o servidor manda o lote
     * como "38266 - 38266" (ou "123 - 4123" no formato antigo). O primeiro que
     * casar vence, então o candidato mais específico precisa vir primeiro.
     */
    async buscarLote(candidatos) {
      for (const candidato of candidatos) {
        if (!candidato) {
          continue;
        }
        const r = await db.execute(
          'select id, eventoId, loteStr, setor, setorId, consumacao ' +
            "from lote where loteStr like '%' || ? || '%' limit 1",
          [candidato],
        );
        const achado = linhas<Lote>(r)[0];
        if (achado) {
          return achado;
        }
      }
      return null;
    },

    async buscarSetor(remoteId) {
      const r = await db.execute(
        'select id, remoteId, eventoId, nome, habilitado from setor where remoteId = ? limit 1',
        [remoteId],
      );
      const linha = linhas<Omit<Setor, 'habilitado'> & {habilitado: number}>(r)[0];
      return linha ? {...linha, habilitado: Boolean(linha.habilitado)} : null;
    },

    async buscarOnline(codbar) {
      const r = await db.execute(
        'select id, eventoId, codbar, nome, email, cpf, pedidoId, status, setor, setorId, numero ' +
          'from ingresso_online where codbar = ? limit 1',
        [codbar],
      );
      return linhas<IngressoOnline>(r)[0] ?? null;
    },

    async registrarValidado(validado) {
      await db.execute(
        'insert or ignore into validado (eventoId, codbar, dataHora, setorId, consumacao, enviado, local) ' +
          'values (?, ?, ?, ?, ?, 0, 1)',
        [
          validado.eventoId,
          validado.codbar,
          validado.dataHora,
          validado.setorId,
          validado.consumacao,
        ],
      );
    },
  };
}

// --- Consultas das telas -----------------------------------------------------

export async function listarEventos(): Promise<Evento[]> {
  const r = await banco().execute(
    'select id, nome, data, local, cidade, banner from evento order by data desc',
  );
  return linhas<Evento>(r);
}

export async function listarSetores(eventoId: number): Promise<Setor[]> {
  const r = await banco().execute(
    'select id, remoteId, eventoId, nome, habilitado from setor where eventoId = ? order by nome',
    [eventoId],
  );
  return linhas<Omit<Setor, 'habilitado'> & {habilitado: number}>(r).map(s => ({
    ...s,
    habilitado: Boolean(s.habilitado),
  }));
}

/** Liga/desliga um setor nesta portaria — é o filtro que a regra nº 5 consulta. */
export async function definirSetorHabilitado(
  remoteId: number,
  habilitado: boolean,
): Promise<void> {
  await banco().execute('update setor set habilitado = ? where remoteId = ?', [
    habilitado ? 1 : 0,
    remoteId,
  ]);
}

export async function definirTodosSetores(
  eventoId: number,
  habilitado: boolean,
): Promise<void> {
  await banco().execute('update setor set habilitado = ? where eventoId = ?', [
    habilitado ? 1 : 0,
    eventoId,
  ]);
}

export interface FiltroIngressos {
  eventoId: number;
  termo?: string;
  status?: string;
  /**
   * Já entrou ou não. Indefinido traz os dois.
   *
   * Conta como validado o que este aparelho registrou e também o que desceu
   * do servidor feito em outra portaria — é a pergunta do portador ("eu já
   * passei?"), e para ela não importa qual catraca leu.
   */
  validado?: boolean;
  limite?: number;
  offset?: number;
}

/** Ingresso da lista, já cruzado com o que este aparelho sabe de validação. */
export interface IngressoListado extends IngressoOnline {
  /** Quando o código consta como validado — deste aparelho ou de outro posto. */
  validadoEm?: string;
  /** true = a validação foi feita neste aparelho. */
  validadoAqui?: boolean;
}

/**
 * Lista de ingressos gerados, todos os status. Com `termo` vira a busca ao
 * portador: nome, e-mail, CPF, pedido ou o próprio código.
 *
 * O `left join` em `validado` é o que responde a pergunta que o portador faz na
 * portaria — "mas eu já não entrei?" — sem uma segunda consulta por linha.
 */
export async function listarIngressos(
  filtro: FiltroIngressos,
): Promise<IngressoListado[]> {
  const {eventoId, termo, status, validado, limite = 50, offset = 0} = filtro;
  const condicoes = ['o.eventoId = ?'];
  const params: Array<string | number> = [eventoId];

  if (termo && termo.trim().length > 0) {
    const curinga = '%' + termo.trim() + '%';
    condicoes.push(
      '(o.nome like ? or o.email like ? or o.cpf like ? or o.pedidoId like ? or o.codbar like ?)',
    );
    params.push(curinga, curinga, curinga, curinga, curinga);
  }
  if (status && status.length > 0) {
    condicoes.push('o.status = ?');
    params.push(status);
  }
  if (validado !== undefined) {
    // O join com `validado` é à esquerda: sem linha lá, o ingresso não entrou.
    condicoes.push(validado ? 'v.dataHora is not null' : 'v.dataHora is null');
  }

  params.push(limite, offset);

  const sql =
    'select o.id, o.eventoId, o.codbar, o.nome, o.email, o.cpf, o.pedidoId, ' +
    'o.status, o.setor, o.setorId, o.numero, ' +
    'v.dataHora as validadoEm, v.local as validadoAqui ' +
    'from ingresso_online o ' +
    'left join validado v on v.codbar = o.codbar and v.eventoId = o.eventoId ' +
    'where ' +
    condicoes.join(' and ') +
    ' order by o.nome limit ? offset ?';

  return linhas<
    Omit<IngressoListado, 'validadoAqui'> & {validadoAqui: number | null}
  >(await banco().execute(sql, params)).map(l => ({
    ...l,
    validadoEm: l.validadoEm ?? undefined,
    validadoAqui: l.validadoAqui === 1,
  }));
}

export async function statusDisponiveis(eventoId: number): Promise<string[]> {
  const r = await banco().execute(
    'select distinct status from ingresso_online where eventoId = ? and status is not null order by status',
    [eventoId],
  );
  return linhas<{status: string}>(r).map(l => l.status);
}

export interface ResumoEvento {
  /**
   * Faixas de lote baixadas. São elas que autorizam o ingresso comum, que não
   * tem linha própria em lugar nenhum: a validade sai do código de barras
   * conferido contra a faixa. Sem lote não se valida nada, então este é o
   * número que diz se a base do evento chegou.
   */
  lotes: number;
  /** Ingressos com portador cadastrado — os que pedem conferência na tela. */
  ingressos: number;
  /** Cancelados e devolvidos, que descem um a um. */
  cancelados: number;
  /** Todas as entradas do evento, somando o que veio dos outros aparelhos. */
  validados: number;
  /** Só o que este aparelho validou — é o número do operador. */
  nestaMaquina: number;
  /** Validações deste aparelho que ainda não subiram. */
  pendentes: number;
}

export async function resumoDoEvento(eventoId: number): Promise<ResumoEvento> {
  const db = banco();
  const [i, v, m, p, l, c] = await Promise.all([
    db.execute('select count(*) as total from ingresso_online where eventoId = ?', [
      eventoId,
    ]),
    db.execute('select count(*) as total from validado where eventoId = ?', [eventoId]),
    db.execute(
      'select count(*) as total from validado where eventoId = ? and local = 1',
      [eventoId],
    ),
    db.execute(
      'select count(*) as total from validado where eventoId = ? and enviado = 0',
      [eventoId],
    ),
    db.execute('select count(*) as total from lote where eventoId = ?', [eventoId]),
    db.execute('select count(*) as total from ingresso_cancelado where eventoId = ?', [
      eventoId,
    ]),
  ]);
  return {
    lotes: Number(linhas<{total: number}>(l)[0]?.total ?? 0),
    ingressos: Number(linhas<{total: number}>(i)[0]?.total ?? 0),
    cancelados: Number(linhas<{total: number}>(c)[0]?.total ?? 0),
    validados: Number(linhas<{total: number}>(v)[0]?.total ?? 0),
    nestaMaquina: Number(linhas<{total: number}>(m)[0]?.total ?? 0),
    pendentes: Number(linhas<{total: number}>(p)[0]?.total ?? 0),
  };
}

export interface LinhaRelatorio {
  setorId: string;
  nome: string;
  quantidade: number;
}

export interface RelatorioCaixa {
  /** Entradas liberadas por este aparelho no evento. */
  total: number;
  /** Quantas ainda não subiram — precisa ser zero para fechar em paz. */
  pendentes: number;
  primeira?: string;
  ultima?: string;
  /** Hora cheia com mais entradas (0-23), para o operador reconhecer o pico. */
  horaPico?: number;
  porSetor: LinhaRelatorio[];
}

/**
 * Relatório de fechamento: o que este aparelho validou, por setor.
 *
 * Conta só `local = 1` — validação que desceu do servidor é de outro posto e
 * não entra na prestação de contas deste caixa.
 */
export async function relatorioDoCaixa(eventoId: number): Promise<RelatorioCaixa> {
  const db = banco();

  const totais = await db.execute(
    'select count(*) as total, ' +
      'sum(case when enviado = 0 then 1 else 0 end) as pendentes, ' +
      'min(dataHora) as primeira, max(dataHora) as ultima ' +
      'from validado where eventoId = ? and local = 1',
    [eventoId],
  );

  // Hora cheia com mais entradas. O `localtime` importa: `dataHora` é gravado
  // em UTC, e o pico que interessa ao operador é o do relógio da portaria.
  const pico = await db.execute(
    "select cast(strftime('%H', dataHora, 'localtime') as integer) as hora, " +
      'count(*) as total from validado ' +
      'where eventoId = ? and local = 1 group by hora order by total desc, hora limit 1',
    [eventoId],
  );

  const porSetor = await db.execute(
    'select v.setorId as setorId, ' +
      "coalesce(s.nome, 'Sem setor') as nome, " +
      'count(*) as quantidade ' +
      'from validado v ' +
      'left join setor s on s.remoteId = cast(v.setorId as integer) ' +
      'where v.eventoId = ? and v.local = 1 ' +
      'group by v.setorId, s.nome ' +
      'order by quantidade desc, nome',
    [eventoId],
  );

  const linha = linhas<{
    total: number;
    pendentes: number | null;
    primeira: string | null;
    ultima: string | null;
  }>(totais)[0];

  return {
    total: Number(linha?.total ?? 0),
    pendentes: Number(linha?.pendentes ?? 0),
    primeira: linha?.primeira ?? undefined,
    ultima: linha?.ultima ?? undefined,
    horaPico: linhas<{hora: number | null}>(pico)[0]?.hora ?? undefined,
    porSetor: linhas<LinhaRelatorio>(porSetor).map(l => ({
      ...l,
      quantidade: Number(l.quantidade),
    })),
  };
}


/** Uma entrada liberada por este aparelho, para o histórico da portaria. */
export interface EntradaDoAcesso {
  id: number;
  codbar: string;
  dataHora: string;
  nome?: string;
  setor?: string;
  /**
   * Camarote, mesa, cadeira. Só existe para ingresso numerado, e só desce no
   * `downloadOnline` — sem essa carga o histórico mostra o setor sem o número.
   */
  numero?: string;
  /** false enquanto o servidor não confirmou o recebimento. */
  enviado: boolean;
}

/**
 * Histórico do acesso: quem este aparelho deixou entrar, do último para o
 * primeiro, em páginas.
 *
 * Ordena por `id`, não por `dataHora`. O id é a ordem real de gravação; o
 * relógio do terminal pode andar para trás — ajuste por rede, troca de fuso — e
 * aí a última entrada, que é justamente a que o operador procura quando alguém
 * reclama, apareceria no meio da lista.
 *
 * Só `local = 1`: validação que desceu do servidor é de outro posto e não é
 * história desta portaria.
 */
export async function historicoDoAcesso(
  eventoId: number,
  limite = 10,
  offset = 0,
): Promise<EntradaDoAcesso[]> {
  const r = await banco().execute(
    'select v.id as id, v.codbar as codbar, v.dataHora as dataHora, ' +
      'v.enviado as enviado, o.nome as nome, o.numero as numero, ' +
      'coalesce(o.setor, s.nome) as setor ' +
      'from validado v ' +
      'left join ingresso_online o on o.codbar = v.codbar and o.eventoId = v.eventoId ' +
      'left join setor s on s.remoteId = cast(v.setorId as integer) ' +
      'where v.eventoId = ? and v.local = 1 ' +
      'order by v.id desc limit ? offset ?',
    [eventoId, limite, offset],
  );

  return linhas<{
    id: number;
    codbar: string;
    dataHora: string;
    enviado: number;
    nome: string | null;
    numero: string | null;
    setor: string | null;
  }>(r).map(l => ({
    id: Number(l.id),
    codbar: l.codbar,
    dataHora: l.dataHora,
    nome: l.nome ?? undefined,
    // String vazia é o normal quando o ingresso não é numerado.
    numero: l.numero?.trim() || undefined,
    setor: l.setor ?? undefined,
    enviado: Number(l.enviado) === 1,
  }));
}

/** Números que a lista de eventos mostra em cada cartão. */
export interface ContagemEvento {
  ingressos: number;
  setores: number;
  /** Entradas que este aparelho liberou neste evento. */
  nestaMaquina: number;
}

/**
 * Contagens de todos os eventos de uma vez.
 *
 * Três agregações em vez de três consultas por cartão: a lista de eventos abre
 * antes de a portaria começar, mas abre com o operador esperando em pé.
 */
export async function contagensDosEventos(): Promise<Record<number, ContagemEvento>> {
  const db = banco();
  const [i, s, m] = await Promise.all([
    db.execute('select eventoId, count(*) as total from ingresso_online group by eventoId'),
    db.execute('select eventoId, count(*) as total from setor group by eventoId'),
    db.execute(
      'select eventoId, count(*) as total from validado where local = 1 group by eventoId',
    ),
  ]);

  const contagens: Record<number, ContagemEvento> = {};
  const somar = (r: Parameters<typeof linhas>[0], campo: keyof ContagemEvento) => {
    for (const linha of linhas<{eventoId: number; total: number}>(r)) {
      const id = Number(linha.eventoId);
      contagens[id] = contagens[id] ?? {ingressos: 0, setores: 0, nestaMaquina: 0};
      contagens[id][campo] = Number(linha.total);
    }
  };

  somar(i, 'ingressos');
  somar(s, 'setores');
  somar(m, 'nestaMaquina');
  return contagens;
}

/** Números por setor, na tela em que o operador decide quem entra por onde. */
export interface ContagemSetor {
  /**
   * Ingressos nominais deste setor. É a mesma base do total do evento — o que
   * não é nominal só existe como faixa de lote e não tem linha para contar.
   */
  ingressos: number;
  /**
   * Entradas liberadas por ESTE aparelho neste setor.
   *
   * Não existe o número geral por setor: validação feita em outra portaria
   * desce do servidor como código de barras nu, sem setor, e é guardada com
   * `setorId` vazio (ver `salvarValidadosDoServidor`). O total de todas as
   * portarias só existe no nível do evento.
   */
  nestaMaquina: number;
}

export async function contagensDosSetores(
  eventoId: number,
): Promise<Record<number, ContagemSetor>> {
  const db = banco();
  const [i, m] = await Promise.all([
    db.execute(
      'select setorId, count(*) as total from ingresso_online where eventoId = ? group by setorId',
      [eventoId],
    ),
    db.execute(
      'select cast(setorId as integer) as setorId, count(*) as total ' +
        'from validado where eventoId = ? and local = 1 group by setorId',
      [eventoId],
    ),
  ]);

  const contagens: Record<number, ContagemSetor> = {};
  const somar = (r: Parameters<typeof linhas>[0], campo: keyof ContagemSetor) => {
    for (const linha of linhas<{setorId: number | null; total: number}>(r)) {
      if (linha.setorId === null) {
        continue;
      }
      const id = Number(linha.setorId);
      contagens[id] = contagens[id] ?? {ingressos: 0, nestaMaquina: 0};
      contagens[id][campo] = Number(linha.total);
    }
  };

  somar(i, 'ingressos');
  somar(m, 'nestaMaquina');
  return contagens;
}

// --- Escrita vinda do sync ---------------------------------------------------

export async function salvarEventos(eventos: Evento[]): Promise<void> {
  const db = banco();
  for (const e of eventos) {
    await db.execute(
      'insert or replace into evento (id, nome, data, local, cidade, banner) ' +
        'values (?, ?, ?, ?, ?, ?)',
      [e.id, e.nome, e.data ?? null, e.local ?? null, e.cidade ?? null, e.banner ?? null],
    );
  }
}

export async function salvarLotes(lotes: Lote[]): Promise<void> {
  const db = banco();
  for (const l of lotes) {
    await db.execute(
      'insert into lote (eventoId, loteStr, setor, setorId, consumacao) values (?, ?, ?, ?, ?) ' +
        'on conflict(loteStr) do update set eventoId = excluded.eventoId, setor = excluded.setor, ' +
        'setorId = excluded.setorId, consumacao = excluded.consumacao',
      [l.eventoId, l.loteStr, l.setor, l.setorId, l.consumacao],
    );
  }
}

/**
 * Setor novo entra DESLIGADO, como no TMobi original: nenhuma portaria começa a
 * aceitar ingresso de um setor que o operador não abriu de propósito. Setor
 * criado no servidor no meio do evento desce no delta e fica esperando na tela
 * de setores até alguém ligar onde for preciso.
 *
 * Setor que já existe preserva a escolha do operador — o `do update` não toca
 * `habilitado`, então ressincronizar não reabre portaria que ele fechou nem
 * desliga a que ele abriu.
 */
export async function salvarSetores(
  setores: Array<Omit<Setor, 'habilitado' | 'id'>>,
): Promise<void> {
  const db = banco();
  for (const s of setores) {
    await db.execute(
      'insert into setor (remoteId, eventoId, nome, habilitado) values (?, ?, ?, ?) ' +
        'on conflict(remoteId) do update set nome = excluded.nome, eventoId = excluded.eventoId',
      [s.remoteId, s.eventoId, s.nome, 0],
    );
  }
}

export async function salvarCancelados(
  cancelados: IngressoCancelado[],
): Promise<void> {
  const db = banco();
  for (const c of cancelados) {
    await db.execute(
      'insert or replace into ingresso_cancelado ' +
        '(eventoId, codbar, dataCancelamento, motivoCancelamento, usuario) values (?, ?, ?, ?, ?)',
      [c.eventoId, c.codbar, c.dataCancelamento, c.motivoCancelamento, c.usuario],
    );
  }
}

export async function salvarIngressosOnline(
  ingressos: IngressoOnline[],
): Promise<void> {
  const db = banco();
  for (const i of ingressos) {
    await db.execute(
      'insert or replace into ingresso_online ' +
        '(numero, eventoId, codbar, nome, cpf, email, setor, setorId, status, pedidoId) ' +
        'values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)',
      [
        i.numero,
        i.eventoId,
        i.codbar,
        i.nome,
        i.cpf,
        i.email,
        i.setor,
        i.setorId,
        i.status,
        i.pedidoId,
      ],
    );
  }
}

/**
 * Validações que vieram do servidor (feitas em outro aparelho). `or ignore`
 * para nunca sobrescrever uma validação local que ainda não subiu.
 */
export async function salvarValidadosDoServidor(
  eventoId: number,
  codbars: string[],
  dataHora: string,
): Promise<void> {
  const db = banco();
  for (const codbar of codbars) {
    await db.execute(
      'insert or ignore into validado (eventoId, codbar, dataHora, setorId, consumacao, enviado, local) ' +
        "values (?, ?, ?, '', '0', 1, 0)",
      [eventoId, codbar, dataHora],
    );
  }
}

export async function validadosPendentes(eventoId: number): Promise<Validado[]> {
  const r = await banco().execute(
    'select id, eventoId, codbar, dataHora, setorId, consumacao, enviado ' +
      'from validado where eventoId = ? and enviado = 0 order by dataHora',
    [eventoId],
  );
  return linhas<Omit<Validado, 'enviado'> & {enviado: number}>(r).map(v => ({
    ...v,
    enviado: Boolean(v.enviado),
  }));
}

export async function marcarComoEnviados(codbars: string[]): Promise<void> {
  if (codbars.length === 0) {
    return;
  }
  const interrogacoes = codbars.map(() => '?').join(', ');
  await banco().execute(
    'update validado set enviado = 1 where codbar in (' + interrogacoes + ')',
    codbars,
  );
}

// --- Quem pode liberar operação restrita ------------------------------------

export interface UsuarioAutorizado {
  remoteId: number;
  nome: string;
  nivel: number;
  senhaHash: string;
}

export async function salvarUsuariosAutorizados(
  usuarios: UsuarioAutorizado[],
): Promise<void> {
  const db = banco();
  const agora = new Date().toISOString();

  for (const u of usuarios) {
    await db.execute(
      'insert or replace into usuario_autorizado ' +
        '(remoteId, nome, nivel, senhaHash, atualizadoEm) values (?, ?, ?, ?, ?)',
      [u.remoteId, u.nome, u.nivel, u.senhaHash, agora],
    );
  }
}

export async function buscarUsuarioAutorizado(
  remoteId: number,
): Promise<UsuarioAutorizado | null> {
  const r = await banco().execute(
    'select remoteId, nome, nivel, senhaHash from usuario_autorizado where remoteId = ? limit 1',
    [remoteId],
  );
  return linhas<UsuarioAutorizado>(r)[0] ?? null;
}

export async function totalUsuariosAutorizados(): Promise<number> {
  const r = await banco().execute('select count(*) as total from usuario_autorizado');
  return Number(linhas<{total: number}>(r)[0]?.total ?? 0);
}

export async function ultimaAtualizacao(eventoId: number): Promise<string> {
  const r = await banco().execute(
    'select ultimaAtualizacao from sync_estado where eventoId = ?',
    [eventoId],
  );
  return linhas<{ultimaAtualizacao: string}>(r)[0]?.ultimaAtualizacao ?? '2000-01-01';
}

/**
 * Quando este aparelho conversou com o servidor pela última vez neste evento.
 * Diferente de `ultimaAtualizacao`, que é o relógio do servidor usado como
 * marca-d'água do delta: este é o relógio local, e serve só para a tela dizer
 * ao operador há quanto tempo a base foi carregada.
 */
export async function ultimaSincronizacao(eventoId: number): Promise<string | undefined> {
  const r = await banco().execute(
    'select ultimaSincronizacao from sync_estado where eventoId = ?',
    [eventoId],
  );
  return linhas<{ultimaSincronizacao: string | null}>(r)[0]?.ultimaSincronizacao ?? undefined;
}

export async function definirUltimaAtualizacao(
  eventoId: number,
  valor: string,
): Promise<void> {
  await banco().execute(
    'insert or replace into sync_estado (eventoId, ultimaAtualizacao, ultimaSincronizacao) values (?, ?, ?)',
    [eventoId, valor, new Date().toISOString()],
  );
}

// --- Troca de operador ------------------------------------------------------

/**
 * Validações deste aparelho que ainda não subiram, de qualquer evento.
 *
 * Diferente de `validadosPendentes`, que é por evento porque o envio é por
 * evento. Esta olha o aparelho inteiro, e existe por causa de um detalhe do
 * agendador: ele é amarrado a um `eventoId` (o do caixa aberto), então não há
 * varredura que encontre pendente de evento que ninguém abriu. Antes de apagar
 * o catálogo é preciso saber se sobrou alguma.
 */
export async function totalPendentesNoAparelho(): Promise<number> {
  // `eventoId >= 0` exclui a demonstração: aquelas validações nunca serão
  // enviadas, e contá-las faria o aviso da troca de operador mentir — pedindo
  // para o operador sincronizar uma fila que não existe.
  const r = await banco().execute(
    'select count(*) as total from validado where enviado = 0 and eventoId >= 0',
  );
  return Number(linhas<{total: number}>(r)[0]?.total ?? 0);
}

/**
 * Apaga o catálogo baixado do servidor, preservando as validações.
 *
 * É a regra do TMobi original: `PanelActivity.logout()` limpa `Evento` e `Lote`
 * e nunca toca em `CValidado` — só um reset completo do banco
 * (`DatabaseHelper.clearTables`) faz isso, e esse caminho não passa pelo
 * logout. Sem a limpeza, o operador seguinte enxerga os eventos do anterior
 * misturados com os dele.
 *
 * O que fica de fora, e por quê:
 *
 * - `validado` — validação não enviada é o único dado deste banco que não
 *   existe em nenhum outro lugar. Ela sobrevive à troca de operador e sobe
 *   quando alguém abrir aquele evento de novo.
 * - `usuario_autorizado` — é a lista de quem pode liberar operação restrita,
 *   e pertence à ticketeira, não ao operador. Apagar aqui deixaria a portaria
 *   sem liberação offline logo na troca de turno, que é justamente quando ela
 *   é pedida.
 *
 * `sync_estado` PRECISA ir junto: ele guarda o relógio do servidor usado como
 * cursor incremental em `sincronizarEvento`. Mantê-lo depois de esvaziar as
 * outras tabelas faria o próximo download pedir "o que mudou desde ontem",
 * receber nada, e deixar o operador novo com evento vazio e sem saída.
 */
export async function limparCatalogo(): Promise<void> {
  const db = banco();
  for (const tabela of [
    'ingresso_online',
    'ingresso_cancelado',
    'lote',
    'setor',
    'evento',
    'sync_estado',
  ]) {
    await db.execute(`delete from ${tabela}`);
  }

  // A única validação que este método apaga é a de demonstração (id negativo).
  // Ela nunca foi enviada e nunca será — guardá-la só faria a demonstração
  // seguinte começar suja, com o primeiro código já dizendo JÁ VALIDADO. A
  // regra de preservar `validado` continua valendo para tudo que é real.
  await db.execute('delete from validado where eventoId < 0');
}

/**
 * Apaga os eventos que o servidor deixou de mandar, junto com o que pendurava
 * neles. É o que faz "desvincular o operador do evento" chegar na portaria no
 * "Atualizar", em vez de só no próximo login.
 *
 * O TMobi original tinha esse algoritmo escrito e COMENTADO em
 * `Syncing.java` (marcar todos com status "P", desmarcar os que voltaram). Vale
 * entender por que, porque o motivo continua de pé: apagar por ausência
 * transforma qualquer resposta pobre do servidor — timeout que devolve lista
 * curta, permissão que oscila — em portaria sem evento no meio do turno.
 *
 * Daí as duas travas. Nenhuma delas é conveniência:
 *
 * - `protegidos` guarda o evento do caixa aberto. Trocar o chão de quem está
 *   validando agora é pior que mostrar um evento a mais.
 * - Evento com validação por enviar nunca sai. Ele é o único caminho de volta
 *   para aquela fila: o agendador é por evento, e sem o evento na lista
 *   ninguém abre a tela que faria a validação subir.
 *
 * Devolve o que saiu e o que ficou retido, para a tela poder explicar ao
 * operador por que aquele evento continua ali.
 */
export async function removerEventosForaDaLista(
  idsDoServidor: number[],
  protegidos: number[] = [],
): Promise<{removidos: number[]; retidosPorPendencia: number[]}> {
  const db = banco();

  const locais = linhas<{id: number}>(await db.execute('select id from evento')).map(
    e => Number(e.id),
  );
  const noServidor = new Set(idsDoServidor);
  const protegido = new Set(protegidos);
  // Id negativo é demonstração e não vem de servidor nenhum: sem esta guarda,
  // uma atualização de lista o trataria como "sumiu do servidor" e o apagaria.
  // A tela já evita chamar isto em demonstração; esta linha é a rede embaixo.
  const candidatos = locais.filter(
    id => id >= 0 && !noServidor.has(id) && !protegido.has(id),
  );

  if (candidatos.length === 0) {
    return {removidos: [], retidosPorPendencia: []};
  }

  const interrogacoes = candidatos.map(() => '?').join(', ');
  const comPendencia = new Set(
    linhas<{eventoId: number}>(
      await db.execute(
        'select distinct eventoId from validado where enviado = 0 ' +
          `and eventoId in (${interrogacoes})`,
        candidatos,
      ),
    ).map(v => Number(v.eventoId)),
  );

  const removidos = candidatos.filter(id => !comPendencia.has(id));

  // `validado` fica de fora: os que sobraram aqui já subiram, e apagar
  // histórico de validação nunca é ganho — no pior caso ele volta a descer do
  // servidor na próxima sincronização. `evento` é o último e usa `id`, não
  // `eventoId`, porque ele é o dono da chave.
  const dependentes: Array<[tabela: string, coluna: string]> = [
    ['ingresso_online', 'eventoId'],
    ['ingresso_cancelado', 'eventoId'],
    ['lote', 'eventoId'],
    ['setor', 'eventoId'],
    ['sync_estado', 'eventoId'],
    ['evento', 'id'],
  ];

  for (const eventoId of removidos) {
    for (const [tabela, coluna] of dependentes) {
      await db.execute(`delete from ${tabela} where ${coluna} = ?`, [eventoId]);
    }
  }

  return {removidos, retidosPorPendencia: [...comPendencia]};
}
